import { useMemo, useState } from 'react';
import {
    FiArrowLeft,
    FiArrowRight,
    FiCheck,
    FiClock,
    FiEdit3,
    FiRefreshCw,
    FiSend,
    FiShield,
    FiZap,
} from 'react-icons/fi';
import { useQuoteSubmission } from '../_Hooks/useQuoteSubmission';

const PROJECT_TYPES = [
    { id: 'new-site', label: 'Sitio web nuevo' },
    { id: 'redesign', label: 'Rediseñar mi sitio' },
    { id: 'ecommerce', label: 'Tienda online' },
    { id: 'landing', label: 'Landing page' },
    { id: 'custom-system', label: 'Sistema a medida' },
    { id: 'not-sure', label: 'Todavía no lo sé' },
];

const PROJECT_STATUS = [
    { id: 'idea', label: 'Tengo una idea' },
    { id: 'current-site', label: 'Ya tengo un sitio' },
    { id: 'design', label: 'Ya tengo diseño' },
    { id: 'guidance', label: 'Necesito asesoramiento' },
];

const PROJECT_TIMING = [
    { id: 'asap', label: 'Lo antes posible' },
    { id: '1-2-months', label: '1 a 2 meses' },
    { id: '2-3-months', label: '2 a 3 meses' },
    { id: 'flexible', label: 'Sin fecha definida' },
];

const INITIAL_QUOTE = {
    projectType: '',
    status: '',
    timing: '',
    name: '',
    contact: '',
    details: '',
};

const getOptionLabel = (options, value) => options.find((item) => item.id === value)?.label || '-';

export const QuoteEstimator = () => {
    const [step, setStep] = useState(1);
    const [completed, setCompleted] = useState(false);
    const [quote, setQuote] = useState(INITIAL_QUOTE);
    const [submission, setSubmission] = useState(null);
    const { submitQuote, isSubmitting, error, clearError } = useQuoteSubmission();

    const progress = useMemo(() => `${Math.min(step, 3)} de 3`, [step]);

    const updateQuote = (field, value) => {
        clearError();
        setQuote((current) => ({ ...current, [field]: value }));
    };

    const resetQuote = () => {
        setQuote(INITIAL_QUOTE);
        setStep(1);
        setCompleted(false);
        setSubmission(null);
        clearError();
    };

    const canContinue = step === 1
        ? Boolean(quote.projectType)
        : step === 2
            ? Boolean(quote.status && quote.timing)
            : Boolean(quote.name.trim() && quote.contact.trim());

    const handlePrimaryAction = async () => {
        if (!canContinue || isSubmitting) return;

        if (step < 3) {
            setStep((current) => current + 1);
            return;
        }

        try {
            const result = await submitQuote({
                project_type: quote.projectType,
                project_stage: quote.status,
                timing: quote.timing,
                customer_name: quote.name.trim(),
                customer_contact: quote.contact.trim(),
                details: quote.details.trim(),
            });
            setSubmission(result || null);
            setCompleted(true);
        } catch (_) {
            // El error se muestra dentro del cotizador para no alterar el resto del Hero.
        }
    };

    if (completed) {
        return (
            <aside className="PB__quote_estimator PB__quote_estimator--complete" aria-label="Resumen de cotización">
                <div className="PB__quote_success_icon"><FiCheck /></div>
                <span className="PB__quote_kicker">Solicitud enviada</span>
                <h2>Ya sé qué necesitás.</h2>
                <p className="PB__quote_intro">
                    Recibí tu solicitud y voy a responderte con una propuesta personalizada.
                    {submission?.quote_code && <strong className="PB__quote_code"> Código: {submission.quote_code}</strong>}
                </p>

                <div className="PB__quote_summary">
                    <div><span>Proyecto</span><strong>{getOptionLabel(PROJECT_TYPES, quote.projectType)}</strong></div>
                    <div><span>Estado</span><strong>{getOptionLabel(PROJECT_STATUS, quote.status)}</strong></div>
                    <div><span>Plazo</span><strong>{getOptionLabel(PROJECT_TIMING, quote.timing)}</strong></div>
                    <div><span>Contacto</span><strong>{quote.contact}</strong></div>
                </div>

                <div className="PB__quote_payment PB__quote_payment--summary">
                    <FiShield />
                    <span><strong>Modalidad de pago</strong>50% antes de comenzar · 50% al finalizar.</span>
                </div>

                <button type="button" className="PB__quote_primary" onClick={resetQuote}>
                    <FiRefreshCw /> Enviar otra consulta
                </button>
            </aside>
        );
    }

    return (
        <aside className="PB__quote_estimator" aria-label="Cotizador de proyecto">
            <div className="PB__quote_topline">
                <div>
                    <span className="PB__quote_kicker">Cotizá tu proyecto</span>
                    <small>Respuesta personalizada</small>
                </div>
                <span className="PB__quote_step">Paso {progress}</span>
            </div>

            <div className="PB__quote_progress" aria-hidden="true">
                {[1, 2, 3].map((item) => (
                    <span key={item} className={item <= step ? 'is-active' : ''} />
                ))}
            </div>

            {step === 1 && (
                <div className="PB__quote_panel">
                    <div className="PB__quote_heading">
                        <span className="PB__quote_heading_icon"><FiZap /></span>
                        <div>
                            <h2>¿Qué necesitás?</h2>
                            <p>Elegí la opción que más se acerque a tu proyecto.</p>
                        </div>
                    </div>
                    <div className="PB__quote_options PB__quote_options--projects">
                        {PROJECT_TYPES.map((option) => (
                            <button
                                key={option.id}
                                type="button"
                                className={quote.projectType === option.id ? 'is-selected' : ''}
                                onClick={() => updateQuote('projectType', option.id)}
                            >
                                {quote.projectType === option.id && <FiCheck />}
                                {option.label}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {step === 2 && (
                <div className="PB__quote_panel">
                    <div className="PB__quote_heading">
                        <span className="PB__quote_heading_icon"><FiClock /></span>
                        <div>
                            <h2>¿En qué etapa estás?</h2>
                            <p>Dos datos rápidos para entender el alcance.</p>
                        </div>
                    </div>

                    <label className="PB__quote_group_label">Situación actual</label>
                    <div className="PB__quote_options PB__quote_options--compact">
                        {PROJECT_STATUS.map((option) => (
                            <button
                                key={option.id}
                                type="button"
                                className={quote.status === option.id ? 'is-selected' : ''}
                                onClick={() => updateQuote('status', option.id)}
                            >
                                {option.label}
                            </button>
                        ))}
                    </div>

                    <label className="PB__quote_group_label PB__quote_group_label--spaced">Plazo ideal</label>
                    <div className="PB__quote_options PB__quote_options--compact">
                        {PROJECT_TIMING.map((option) => (
                            <button
                                key={option.id}
                                type="button"
                                className={quote.timing === option.id ? 'is-selected' : ''}
                                onClick={() => updateQuote('timing', option.id)}
                            >
                                {option.label}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {step === 3 && (
                <div className="PB__quote_panel">
                    <div className="PB__quote_heading">
                        <span className="PB__quote_heading_icon"><FiEdit3 /></span>
                        <div>
                            <h2>Último paso</h2>
                            <p>Dejá tus datos y una breve idea del proyecto.</p>
                        </div>
                    </div>

                    <div className="PB__quote_fields">
                        <label>
                            <span>Nombre</span>
                            <input
                                type="text"
                                value={quote.name}
                                onChange={(event) => updateQuote('name', event.target.value)}
                                placeholder="¿Cómo te llamás?"
                                maxLength={180}
                            />
                        </label>
                        <label>
                            <span>Email o WhatsApp</span>
                            <input
                                type="text"
                                value={quote.contact}
                                onChange={(event) => updateQuote('contact', event.target.value)}
                                placeholder="¿Dónde te respondo?"
                                maxLength={190}
                            />
                        </label>
                        <label className="PB__quote_field--wide">
                            <span>Contame un poco más <small>(opcional)</small></span>
                            <textarea
                                rows="3"
                                value={quote.details}
                                onChange={(event) => updateQuote('details', event.target.value)}
                                placeholder="Objetivo, funcionalidades o cualquier detalle importante..."
                                maxLength={5000}
                            />
                        </label>
                    </div>
                </div>
            )}

            <div className="PB__quote_payment">
                <FiShield />
                <span><strong>Pago simple</strong>50% antes de comenzar · 50% al finalizar.</span>
            </div>

            {error && <div className="PB__quote_error" role="alert">{error.message}</div>}

            <div className="PB__quote_actions">
                {step > 1 ? (
                    <button
                        type="button"
                        className="PB__quote_back"
                        onClick={() => {
                            clearError();
                            setStep((current) => current - 1);
                        }}
                        disabled={isSubmitting}
                    >
                        <FiArrowLeft /> Atrás
                    </button>
                ) : (
                    <span className="PB__quote_response"><FiClock /> Respuesta el mismo día</span>
                )}

                <button
                    type="button"
                    className="PB__quote_primary"
                    disabled={!canContinue || isSubmitting}
                    onClick={handlePrimaryAction}
                >
                    {step < 3
                        ? <>Continuar <FiArrowRight /></>
                        : isSubmitting
                            ? <>Enviando...</>
                            : <>Enviar solicitud <FiSend /></>}
                </button>
            </div>
        </aside>
    );
};
